import React, { useState, useEffect, useCallback } from 'react';
import { Maximize2, X } from 'lucide-react';
import desktopFacturas from '../images/desktop/screen-FACTURAS.png';
import desktopDetail from '../images/desktop/screen-detail.png';
import desktopSettings from '../images/desktop/screen-settings.png';
import invoiceWorkflowGif from '../images/showcase/desktop-invoice-workflow.gif';
import operationsWorkflowGif from '../images/showcase/desktop-operations-workflow.gif';
import purchasesWorkflowGif from '../images/showcase/desktop-purchases-workflow.gif';
import desktopPurchasesPoster from '../images/showcase/desktop-purchases-poster.png';

const SHOTS = [
  {
    src: invoiceWorkflowGif,
    poster: desktopFacturas,
    title: 'Facturas',
    eyebrow: 'DTE · clientes · estados',
    alt: 'Flujo de facturación en escritorio con varias facturas, creación de DTE y detalle del documento',
  },
  {
    src: operationsWorkflowGif,
    poster: desktopDetail,
    title: 'Trabajo administrativo',
    eyebrow: 'Clientes · catálogo · acciones',
    alt: 'Flujo administrativo en escritorio con clientes, productos, detalle de factura y menú de acciones',
  },
  {
    src: purchasesWorkflowGif,
    poster: desktopPurchasesPoster,
    title: 'Compras y cierres',
    eyebrow: 'Compras · exportación · contingencia',
    alt: 'Flujo de compras, exportación y contingencia en la aplicación de escritorio',
  },
  {
    src: null,
    poster: desktopSettings,
    title: 'Modo contingencia',
    eyebrow: 'Operación sin conexión',
    alt: 'Modo contingencia en escritorio con facturas seleccionadas para reportar a Hacienda',
  },
];

export default function DesktopShowcase() {
  const [activeIndex, setActiveIndex] = useState(null);
  const isOpen = activeIndex !== null;

  const close = useCallback(() => setActiveIndex(null), []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') setActiveIndex((i) => (i + 1) % SHOTS.length);
      if (e.key === 'ArrowLeft')  setActiveIndex((i) => (i - 1 + SHOTS.length) % SHOTS.length);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, close]);

  return (
    <section id="capturas" className="bg-white py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-2xl mx-auto text-center mb-12">
          <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-600 mb-4">Windows · macOS</span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-ink-950 mb-4 text-balance">
            Pensado para escritorio.
          </h2>
          <p className="text-lg text-ink-500 leading-relaxed">
            Tablas densas, formularios productivos, atajos de teclado. Lo que tu equipo administrativo necesita.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {SHOTS.map((shot, i) => (
            <button
              key={shot.title}
              type="button"
              onClick={() => setActiveIndex(i)}
              className="group relative overflow-hidden rounded-xl border border-ink-200 bg-white shadow-sm hover:border-brand-500/40 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-zoom-in text-left"
              aria-label={`Ampliar: ${shot.title}`}
            >
              <ShowcaseMedia shot={shot} className="aspect-[16/10] w-full object-cover" />
              <div className="border-t border-ink-100 bg-white px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-600">{shot.eyebrow}</p>
                <h3 className="mt-1 text-[15px] font-bold text-ink-950">{shot.title}</h3>
              </div>
              <div className="absolute inset-0 bg-ink-950/0 group-hover:bg-ink-950/10 transition-colors flex items-center justify-center pointer-events-none">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 backdrop-blur text-ink-900 w-10 h-10 rounded-full shadow-lg flex items-center justify-center">
                  <Maximize2 size={17} strokeWidth={2.2} />
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-8"
          style={{ backgroundColor: 'rgba(2,6,23,0.85)', backdropFilter: 'blur(8px)' }}
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Cerrar"
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 border border-white/15 text-white transition cursor-pointer z-10"
          >
            <X size={20} strokeWidth={2} />
          </button>

          <div className="absolute top-5 left-5 text-white/70 text-[12px] font-mono">
            {activeIndex + 1} / {SHOTS.length}
          </div>

          <ShowcaseMedia
            shot={SHOTS[activeIndex]}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full rounded-xl shadow-2xl border border-white/10 cursor-default"
          />

          <p className="absolute bottom-5 inset-x-0 text-center text-white/55 text-[12px]">
            ESC o clic afuera para cerrar  ·  ← → para navegar
          </p>
        </div>
      )}
    </section>
  );
}

function ShowcaseMedia({ shot, className, onClick }) {
  const desktopSrc = shot.src || shot.poster;

  return (
    <picture onClick={onClick}>
      {shot.src && <source media="(min-width: 768px)" srcSet={desktopSrc} />}
      <img
        src={shot.poster}
        alt={shot.alt}
        className={`block ${className}`}
        loading="lazy"
        decoding="async"
      />
    </picture>
  );
}
